import {
  MarketShippingConfiguration,
  MarketShippingProfileDtoSchema,
  MarketShippingRateGroup,
  MarketShippingRateSchema,
  MarketShippingSelection,
  ShippingMoneySchema,
  ShippingProfileDto,
  ShippingTestMethodDefinitionDto,
} from "@intelligems/ig-types";
import { marketShippingSelectionKey } from "@intelligems/ig-utils";
import { z } from "zod/v4";
import type { BeforeTestField, BeforeTestLabels } from "./BeforeTestNote";
import { optionDisplayName } from "./selection";

const journalGroupSchema = z.object({
  id: z.string(),
  conditions: z.object({
    collectionIds: z.array(z.string()).nullable(),
    originLocationIds: z.array(z.string()),
  }),
  rates: z.array(MarketShippingRateSchema),
  carrier: z
    .object({
      carrierServiceId: z.string(),
      absoluteAdjustment: ShippingMoneySchema.nullable(),
      percentageAdjustment: z.number().nullable(),
      autoIncludeNewServices: z.boolean(),
      serviceConfiguration: z.array(
        z.object({ name: z.string(), status: z.string() })
      ),
    })
    .optional(),
});

function originalGroup(value: unknown): MarketShippingRateGroup | null {
  const parsed = journalGroupSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }
  const group = parsed.data;
  return {
    id: group.id,
    conditions: {
      collections:
        group.conditions.collectionIds?.map((id) => ({ id, title: id })) ??
        null,
      originLocations: group.conditions.originLocationIds.map((id) => ({
        id,
        name: id,
      })),
    },
    rates: group.rates,
    ...(group.carrier
      ? {
          carrier: {
            carrierService: { id: group.carrier.carrierServiceId, name: null },
            absoluteAdjustment: group.carrier.absoluteAdjustment,
            percentageAdjustment: group.carrier.percentageAdjustment,
            autoIncludeNewServices: group.carrier.autoIncludeNewServices,
            serviceConfiguration: group.carrier.serviceConfiguration,
          },
        }
      : {}),
  };
}

/** Build a display-only view. Verify every applied field against live data before restoring any original values. */
export function projectActiveSelectionReview(
  markets: MarketShippingConfiguration[],
  selections: ShippingTestMethodDefinitionDto[],
  backups: ShippingProfileDto[],
  experienceId: string
) {
  const reviewMarkets = structuredClone(markets);
  const beforeTestLabels: BeforeTestLabels = {};
  const issues: string[] = [];
  for (const market of markets) {
    for (const option of market.shipping?.options ?? []) {
      const targets = selections.flatMap((selection) =>
        selection.shippingModel === "markets" &&
        selection.marketTarget.marketId === market.id &&
        selection.marketTarget.optionId === option.id
          ? [selection.marketTarget]
          : []
      );
      if (!targets.length) {
        continue;
      }
      const records = backups.filter(
        (backup) =>
          backup.shippingModel === "markets" &&
          backup.experienceId === experienceId &&
          backup.marketTarget.marketId === market.id &&
          backup.marketTarget.optionId === option.id &&
          backup.marketSnapshot.status !== "restored"
      );
      if (!records.length) {
        continue;
      }
      const changes: Array<{
        field: "isActive" | "freeDeliveryMinimumValue" | "group" | "rate";
        target: MarketShippingSelection;
        value: unknown;
      }> = [];
      let conflict: string | undefined;
      for (const record of records) {
        const parsed = MarketShippingProfileDtoSchema.safeParse(record);
        if (!parsed.success) {
          conflict = "Backup data is invalid";
          break;
        }
        const { marketTarget: target, marketSnapshot: snapshot } = parsed.data;
        if (target.kind === "unresolved_option") {
          conflict = "Shopify option creation is not resolved";
          break;
        }
        if (snapshot.status !== "applied" || snapshot.conflict) {
          conflict = snapshot.conflict || `Backup status is ${snapshot.status}`;
          break;
        }
        if (target.optionType !== option.optionType) {
          conflict = "Shipping option type changed";
          break;
        }
        for (const field of snapshot.changedFields) {
          const original = snapshot.original[field];
          const expected = snapshot.expected[field];
          const group = option.rateGroups.find(
            (item) => item.id === target.rateGroupId
          );
          if (
            field === "isActive" &&
            original === true &&
            expected === false &&
            option.isActive === false
          ) {
            changes.push({ field, target, value: true });
          } else if (
            field === "freeDeliveryMinimumValue" &&
            expected === null &&
            option.freeDeliveryMinimumValue === null &&
            ShippingMoneySchema.safeParse(original).success
          ) {
            changes.push({
              field,
              target,
              value: ShippingMoneySchema.parse(original),
            });
          } else if (
            field === "group" &&
            target.kind === "rate_group" &&
            expected === null &&
            !group
          ) {
            const value = originalGroup(original);
            if (!value || value.id !== target.rateGroupId) {
              conflict = "Original rate group cannot be verified";
              break;
            }
            changes.push({ field, target, value });
          } else if (
            field === "rate" &&
            target.kind === "rate" &&
            expected === null &&
            !group?.rates.some((rate) => rate.id === target.rateId)
          ) {
            const value = MarketShippingRateSchema.safeParse(original);
            if (!value.success || value.data.id !== target.rateId) {
              conflict = "Original rate cannot be verified";
              break;
            }
            changes.push({ field, target, value: value.data });
          } else {
            conflict = `Current ${field} does not match the recorded expected state`;
            break;
          }
        }
        if (conflict) {
          break;
        }
      }
      const seen = new Set<string>();
      for (const change of changes) {
        const key = JSON.stringify([
          change.field,
          change.target.rateGroupId,
          change.target.rateId,
        ]);
        if (seen.has(key)) {
          conflict = "More than one applied backup records the same field";
        }
        seen.add(key);
        if (
          change.field === "rate" &&
          !option.rateGroups.some(
            (group) => group.id === change.target.rateGroupId
          ) &&
          !changes.some(
            (other) =>
              other.field === "group" &&
              other.target.rateGroupId === change.target.rateGroupId
          )
        ) {
          conflict =
            "The selected rate group is missing without a verified removal";
        }
      }
      if (conflict) {
        issues.push(
          `Shipping backup conflict for ${optionDisplayName(option)}: ${conflict}.`
        );
        continue;
      }
      const reviewOption = reviewMarkets
        .find((item) => item.id === market.id)!
        .shipping!.options.find((item) => item.id === option.id)!;
      for (const change of [...changes].sort(
        (a, b) => Number(b.field === "group") - Number(a.field === "group")
      )) {
        if (change.field === "isActive") {
          reviewOption.isActive = true;
        } else if (change.field === "freeDeliveryMinimumValue") {
          reviewOption.freeDeliveryMinimumValue = ShippingMoneySchema.parse(
            change.value
          );
        } else if (change.field === "group") {
          reviewOption.rateGroups.push(change.value as MarketShippingRateGroup);
        } else if (change.field === "rate") {
          reviewOption.rateGroups
            .find((group) => group.id === change.target.rateGroupId)!
            .rates.push(MarketShippingRateSchema.parse(change.value));
        }
      }
      for (const target of targets) {
        const fields = changes
          .filter(
            (change) =>
              change.field === "isActive" ||
              change.field === "freeDeliveryMinimumValue" ||
              target.kind === "option" ||
              (change.target.rateGroupId === target.rateGroupId &&
                (change.field === "group" ||
                  target.kind === "rate_group" ||
                  change.target.rateId === target.rateId))
          )
          .map((change) => change.field as BeforeTestField);
        if (fields.length) {
          beforeTestLabels[marketShippingSelectionKey(target)] = [
            ...new Set(fields),
          ];
        }
      }
    }
  }
  return { reviewMarkets, beforeTestLabels, issues };
}
